import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";

/* Storage can throw (private mode with storage blocked, a sandboxed frame), and
   these calls sit in the context every screen reads — so a throw here took the
   whole app down. A failed read is "nothing remembered"; a failed write keeps
   the context for this visit (K24). */
const store = {
  getItem(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem(key: string, value: string) {
    try { localStorage.setItem(key, value); } catch { /* this visit only */ }
  },
  removeItem(key: string) {
    try { localStorage.removeItem(key); } catch { /* nothing to remove */ }
  },
};

export type UIMode = 'global' | 'project' | 'company-admin';

interface UIContextState {
  mode: UIMode;
  projectId?: string;
  projectName?: string;
  accentColor: string;
}

interface UIContextValue extends UIContextState {
  setGlobalContext: () => void;
  setProjectContext: (projectId: string, projectName: string) => void;
  setProjectId: (projectId: string) => void;
  setCompanyAdminContext: () => void;
  clearContext: () => void;
}

const UIContext = createContext<UIContextValue | undefined>(undefined);

const GLOBAL_ACCENT = '#7C3AED'; // purple-600
const PROJECT_ACCENT = '#4F46E5'; // indigo-600
const COMPANY_ADMIN_ACCENT = '#10B981'; // emerald-500

export function UIProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UIContextState>(() => {
    if (typeof window === 'undefined') {
      return { mode: 'global', accentColor: GLOBAL_ACCENT };
    }

    // Read projectId from URL first — most reliable for direct navigation and refresh.
    // This avoids a blank-page flash when queries have `enabled: !!projectId` and
    // localStorage hasn't caught up yet (e.g. navigating to a different project).
    const urlMatch = window.location.pathname.match(/\/projects\/([^/]+)/);
    const urlProjectId = urlMatch?.[1];

    const lastContext = store.getItem('lastContext');
    const selectedProject = store.getItem('selectedProject');
    const selectedProjectName = store.getItem('selectedProjectName');

    if (urlProjectId && urlProjectId !== 'undefined') {
      return {
        mode: 'project',
        projectId: urlProjectId,
        // Reuse stored name only when it matches the URL project to avoid stale display
        projectName: selectedProject === urlProjectId ? selectedProjectName || undefined : undefined,
        accentColor: PROJECT_ACCENT,
      };
    }

    if (lastContext === 'admin') {
      return { mode: 'global', accentColor: GLOBAL_ACCENT };
    } else if (lastContext?.startsWith('project:') && selectedProject) {
      return {
        mode: 'project',
        projectId: selectedProject,
        projectName: selectedProjectName || undefined,
        accentColor: PROJECT_ACCENT,
      };
    }

    return { mode: 'global', accentColor: GLOBAL_ACCENT };
  });

  /* This used to write `state.accentColor` — a HEX string — into `--accent`,
     a token every `accent` utility reads as an HSL triplet
     (`hsl(var(--accent) / α)`). The result was invalid CSS, so every
     bg-accent / text-accent / from-accent in the app silently rendered
     nothing (found 2026-09-27: the project cards' cover gradient). Nothing
     reads accentColor, so the token is left to index.css. */

  /* ════════════════════════════════════════════════════════════════════════
     WHY EVERY SETTER IS useCallback'd AND BAILS OUT
     ════════════════════════════════════════════════════════════════════════
     These used to be plain functions in the provider body that called
     setState with a fresh object literal and no comparison. That combination
     produced an infinite render loop on every /admin/* route:

       1. App.tsx's route-context effect lists these setters in its dependency
          array. A plain function in a component body gets a NEW identity on
          every render, so the dependency changed every time.
       2. The effect therefore re-ran, and on an /admin route its body calls
          setGlobalContext() unconditionally.
       3. setState({...}) with a brand-new object is never Object.is-equal to
          the previous state, so React could not bail out — the provider
          re-rendered, minting new setter identities, and (1) started again.

     React capped it and logged "Maximum update depth exceeded" ~28 times per
     load. It was not fatal — the page rendered — which is exactly why it
     survived: the only visible cost was a hot main thread, a burst of
     synchronous localStorage writes, and battery.

     The same effect had a second unstable dependency, `projectsData = []` from
     a useQuery default (fixed at its own call site in App.tsx). Two
     independent sources, one loop.

     So: useCallback fixes the identity, and returning `prev` when nothing
     changed makes each setter IDEMPOTENT — safe to call from an effect any
     number of times, which is the property that stops this class of bug coming
     back regardless of what a future caller's dependencies do. `setProjectId`
     always had the bail-out; the other four did not.
     ──────────────────────────────────────────────────────────────────────── */

  const setGlobalContext = useCallback(() => {
    setState(prev =>
      prev.mode === 'global' && prev.projectId === undefined && prev.projectName === undefined
        ? prev
        : { mode: 'global', accentColor: GLOBAL_ACCENT },
    );
    store.setItem('lastContext', 'admin');
    store.removeItem('selectedProject');
    store.removeItem('selectedProjectName');
  }, []);

  const setProjectContext = useCallback((projectId: string, projectName: string) => {
    setState(prev =>
      prev.mode === 'project' && prev.projectId === projectId && prev.projectName === projectName
        ? prev
        : { mode: 'project', projectId, projectName, accentColor: PROJECT_ACCENT },
    );
    store.setItem('lastContext', `project:${projectId}`);
    store.setItem('selectedProject', projectId);
    store.setItem('selectedProjectName', projectName);
  }, []);

  const setProjectId = useCallback((newProjectId: string) => {
    setState(prev => {
      if (prev.projectId === newProjectId && prev.mode === 'project') return prev;
      return {
        mode: 'project',
        projectId: newProjectId,
        // Preserve name only if it belongs to this same project
        projectName: prev.projectId === newProjectId ? prev.projectName : undefined,
        accentColor: PROJECT_ACCENT,
      };
    });
  }, []);

  const setCompanyAdminContext = useCallback(() => {
    setState(prev => (prev.mode === 'company-admin' ? prev : { mode: 'company-admin', accentColor: COMPANY_ADMIN_ACCENT }));
    store.setItem('lastContext', 'company-admin');
    store.removeItem('selectedProject');
    store.removeItem('selectedProjectName');
  }, []);

  const clearContext = useCallback(() => {
    setState(prev =>
      prev.mode === 'global' && prev.projectId === undefined && prev.projectName === undefined
        ? prev
        : { mode: 'global', accentColor: GLOBAL_ACCENT },
    );
    store.removeItem('lastContext');
    store.removeItem('selectedProject');
    store.removeItem('selectedProjectName');
  }, []);

  /* The value was an inline object literal, so it was a new reference on every
     provider render and EVERY consumer of useUIContext() re-rendered with it —
     AppShell, AppSidebar, Breadcrumbs and every page that reads projectId.
     With the setters now stable, the only thing that should invalidate this is
     an actual state change. */
  const value = useMemo(
    () => ({
      ...state,
      setGlobalContext,
      setProjectContext,
      setProjectId,
      setCompanyAdminContext,
      clearContext,
    }),
    [state, setGlobalContext, setProjectContext, setProjectId, setCompanyAdminContext, clearContext],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

/** The context where one is mounted, `undefined` where not — for shared ui/
    primitives (PageHeader) that also render in the harness galleries, which
    mount no UIProvider. Screens should use `useUIContext`. */
export function useOptionalUIContext() {
  return useContext(UIContext);
}

export function useUIContext() {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUIContext must be used within a UIProvider');
  }
  return context;
}
